import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SectionTitle from '@/components/base/SectionTitle';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getNewsById, getRelatedNews, formatDate } from '@/utils/media';

export default function NewsDetail() {
  const { id = '' } = useParams();
  const item = getNewsById(id);

  usePageMeta(
    item ? `${item.title} - 嫩草影院资讯` : '资讯不存在 - 嫩草影院',
    item ? item.summary : '很抱歉，你访问的资讯不存在或已删除。',
    item ? `${item.type},${item.tags.join(',')},嫩草影院` : '影视资讯,嫩草影院',
  );

  if (!item) {
    return (
      <div className="flex min-h-screen flex-col bg-background-950">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-900 text-foreground-500">
              <i className="ri-error-warning-line text-4xl" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-50">
              资讯不存在或已删除
            </h1>
            <p className="mt-2 max-w-md text-sm text-foreground-400">
              这条资讯可能已被移除，返回资讯列表看看其他内容吧。
            </p>
            <Link
              to="/news"
              className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const paragraphs = item.content.split('\n\n');
  const related = getRelatedNews(item, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/news" className="transition-colors hover:text-primary-400">
              影视资讯
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="line-clamp-1 text-foreground-300">{item.type}</span>
          </nav>

          <article className="mt-5">
            <span className="rounded-md bg-secondary-500/20 px-2.5 py-1 text-xs font-medium text-secondary-300">
              {item.type}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-extrabold leading-snug text-foreground-50 md:text-3xl">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-800 pb-5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {item.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {formatDate(item.publishTime)} 发布
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                <i className="ri-price-tag-3-line" />
                {item.tags.join(' · ')}
              </span>
            </div>

            <div className="mt-6 w-full overflow-hidden rounded-lg border border-background-800 bg-background-900">
              <img
                src={item.image}
                alt={item.title}
                title={`${item.title} - 嫩草影院资讯`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4">
              <p className="rounded-lg bg-background-900 px-4 py-3 text-sm leading-7 text-foreground-300">
                {item.summary}
              </p>
              {paragraphs.map((p, i) => (
                <p key={i} className="text-sm leading-8 text-foreground-300 md:text-base">
                  {p}
                </p>
              ))}
            </div>
          </article>

          <section className="mt-12">
            <SectionTitle
              title="相关文章"
              subtitle="你可能还感兴趣"
              icon="ri-article-line"
              moreTo="/news"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/news/${r.id}`}
                  className="group flex gap-3 rounded-lg border border-background-800 bg-background-900 p-3 transition-colors hover:border-primary-600"
                >
                  <div className="h-[68px] w-[100px] shrink-0 overflow-hidden rounded-md bg-background-800">
                    <img
                      src={r.image}
                      alt={r.title}
                      title={`${r.title} - 嫩草影院资讯`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-100 transition-colors group-hover:text-primary-400">
                      {r.title}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-500">
                      {formatDate(r.publishTime)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <div className="mt-10 flex justify-center">
            <Link
              to="/news"
              className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-6 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-600 hover:text-primary-400"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}